"use client";
import { faAngleLeft, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useMemo, useState } from "react";

import Loading from "components/Loading";

/**
 * 親要素にフィットしたサイズになります
 * @param imageUrls string[]
 * @return
 */
const Carousel = ({
  imageUrls,
  width = "w-full",
  height = "h-full",
}: {
  imageUrls: string[];
  width?: string;
  height?: string;
}) => {
  const [currentImage, setCurrentImage] = useState(0);

  const updateImage = (type: "right" | "left") => {
    return type === "right"
      ? setCurrentImage((prev) => (prev + 1) % imageUrls.length)
      : setCurrentImage((prev) => (prev - 1) % imageUrls.length);
  };

  const clickRightDisabled = currentImage === imageUrls.length - 1;
  const clickLeftDisabled = currentImage === 0;

  const pagination = useMemo(() => {
    const imageCount = imageUrls.length;
    // 画像が1枚の場合は表示しない
    if (imageCount === 1) return [];

    // 画像が2枚の場合は、0,1を表示
    // 画像が3枚以上の場合は、currentImageの前後を表示
    // 画像が3枚以上の場合で、currentImageが0の場合は、0,1,2を表示
    // 画像が3枚以上の場合で、currentImageがimageCount-1の場合は、imageCount-3,imageCount-2,imageCount-1を表示
    return imageCount == 2
      ? [0, 1]
      : currentImage == 0
      ? [currentImage, currentImage + 1, currentImage + 2]
      : currentImage == imageCount - 1
      ? [currentImage - 2, currentImage - 1, currentImage]
      : [currentImage - 1, currentImage, currentImage + 1];
  }, [currentImage, imageUrls.length]);

  return (
    <div className={clsx("relative", height, width)}>
      {imageUrls.length > 1 && (
        <>
          <button
            className={clsx(
              "absolute right-3 top-[50%] z-10 flex h-5 w-5 origin-center -translate-y-1/2 items-center justify-center rounded-full bg-white",
              clickRightDisabled && "opacity-40"
            )}
            onClick={() => updateImage("right")}
            disabled={clickRightDisabled}
          >
            <FontAwesomeIcon icon={faAngleRight} />
          </button>
          <button
            className={clsx(
              "absolute left-3 top-[50%] z-10 flex h-5 w-5 origin-center -translate-y-1/2 items-center justify-center rounded-full bg-white",
              clickLeftDisabled && "opacity-40"
            )}
            onClick={() => updateImage("left")}
            disabled={clickLeftDisabled}
          >
            <FontAwesomeIcon icon={faAngleLeft} />
          </button>
          <div className="absolute bottom-1 right-[50%] z-20 origin-center translate-x-1/2">
            <div className="flex gap-2">
              {pagination.map((page) => {
                return (
                  <button
                    className={clsx(
                      "h-[6px] w-[6px] rounded-full",
                      currentImage === page ? "bg-attention" : "bg-white"
                    )}
                    key={page}
                    onClick={() => setCurrentImage(page)}
                  />
                );
              })}
            </div>
          </div>
        </>
      )}
      <div className="relative h-full w-full overflow-hidden transition-all duration-1000">
        <div className="absolute flex h-full w-full items-center justify-center">
          <Loading color="green" height="20px" />
        </div>
        {imageUrls.length > 0 ? (
          imageUrls.map((url, i) => {
            return (
              <div
                className={clsx(
                  "absolute h-full w-full",
                  currentImage < i && "absolute left-full",
                  currentImage > i && "absolute right-full"
                )}
                key={i}
              >
                {url != "" ? (
                  <img src={url} alt={"区画写真" + i} className="h-full w-full object-cover" />
                ) : (
                  <div className="absolute flex h-full w-full items-center justify-center">
                    <img
                      src={"/svg/noImage.svg"}
                      alt={"Not 区画写真"}
                      className="absolute h-full w-full object-cover "
                    />
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="absolute flex h-full w-full items-center justify-center">
            <img src={"/svg/noImage.svg"} alt={"Not 区画写真"} className="absolute h-full w-full object-cover" />
          </div>
        )}
      </div>
    </div>
  );
};

export default Carousel;
